import { EventType, useI18N } from "@sundae/react-hooks";
import { useWalletDetails } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import {
  EContractVersion,
  EDatumType,
  ESwapType,
  IComposedTx,
  IOrderRouteSwapArgs,
  ISwapConfigArgs,
  TUTXO,
} from "@sundaeswap/core";
import { useMutation } from "@tanstack/react-query";
import type { Tx, TxComplete } from "lucid-cardano";
import { useMemo } from "react";

import { PoolFragmentFragment } from "../../../gql/generated/stats2.sdk";
import { IPendingOrder, useSubmittedOrdersCache } from "../../../gql/hooks/operations.query";
import { usePoolFromOrderRoute } from "../../../hooks/exchange/usePoolFromOrderRoute";
import { useEventTracking } from "../../../hooks/useEventTracking";
import {
  ITransactionErrorShape,
  useTransactionErrorHandler,
} from "../../../hooks/useTransactionErrorHandler";
import { Actions, PersistentActions, useAppContext, useProfileAppContext } from "../../../stores";
import { EOrderFlowState, OrderActions, useOrderContext } from "../../../stores/OrderContext";
import { EOrderTransactionBuilderState, EOrderType } from "../../../stores/OrderContext/types";
import { IAssetMetaData } from "../../../types/Asset.types";
import { EOpTypename } from "../../../types/Operation.types";
import { isSameAsset } from "../../../utils/assets.utils";
import { checkIsEternlDappBridgeAvailable } from "../../../utils/network.utils";
import { TransactionErrorHandler } from "../../../utils/transactions/TransactionErrorHandler.class";
import { getEternlFeeAddress, getEternlFeeAmount } from "../../../vendor/eternl-referral-fee";

const getPoolForArgs = (pool: PoolFragmentFragment) => {
  return {
    assetA: pool.assetA,
    assetB: pool.assetB,
    assetLP: pool.assetLP,
    currentFee: Number(pool.fee) / 100,
    ident: pool.ident,
    liquidity: {
      aReserve: BigInt(pool.quantityA || 0),
      bReserve: BigInt(pool.quantityB || 0),
      lpTotal: BigInt(pool.quantityLP || 0),
    },
    version: pool.version,
  };
};

const getSinglePoolRouteArgs = ({
  given,
  isLimitOrder,
  mainAddress,
  orderRoute,
  slippageTolerance,
  taken,
}: {
  given: AssetAmount<IAssetMetaData>;
  isLimitOrder: boolean;
  mainAddress: string;
  orderRoute: PoolFragmentFragment[];
  slippageTolerance: number;
  taken: AssetAmount<IAssetMetaData>;
}) => {
  const pool = orderRoute[0];
  const args: ISwapConfigArgs = {
    orderAddresses: {
      DestinationAddress: {
        address: mainAddress,
        datum: {
          type: EDatumType.NONE,
        },
      },
    },
    pool: getPoolForArgs(pool),
    suppliedAsset: given,
    swapType: isLimitOrder
      ? {
          type: ESwapType.LIMIT,
          minReceivable: taken,
        }
      : {
          type: ESwapType.MARKET,
          slippage: slippageTolerance / 100,
        },
  };
  return args;
};

const getMultiPoolRouteArgs = ({
  given,
  mainAddress,
  orderRoute,
  slippageTolerance,
}: {
  given: AssetAmount<IAssetMetaData>;
  mainAddress: string;
  orderRoute: PoolFragmentFragment[];
  slippageTolerance: number;
}) => {
  const args: IOrderRouteSwapArgs = {
    ownerAddress: mainAddress,
    swapA: {
      swapType: {
        type: ESwapType.MARKET,
        slippage: slippageTolerance / 100,
      },
      pool: getPoolForArgs(orderRoute[0]),
      suppliedAsset: given,
    },
    swapB: {
      swapType: {
        type: ESwapType.MARKET,
        slippage: slippageTolerance / 100,
      },
      pool: getPoolForArgs(orderRoute[1]),
    },
  };
  return args;
};

export const getSwapQuantitiesForOrders = ({
  estimatedInput,
  pool,
  inputAsset,
}: {
  estimatedInput: AssetAmount;
  pool?: PoolFragmentFragment;
  inputAsset?: IAssetMetaData;
}): { swapQuantityA: string | null; swapQuantityB: string | null } => {
  const quantity = estimatedInput.amount.toString();
  if (isSameAsset(inputAsset, pool?.assetA)) {
    return { swapQuantityA: quantity, swapQuantityB: null };
  }
  return { swapQuantityB: quantity, swapQuantityA: null };
};

export interface IUseSwapTxArgs {
  utxo?: TUTXO;
  address?: string;
}

export const useSwapTx = ({ utxo, address }: IUseSwapTxArgs) => {
  const {
    state: { sdk },
    dispatch: appDispatch,
  } = useAppContext();
  const { t } = useI18N("transactions");
  const {
    state: { slippageTolerance },
    dispatch: profileContextDispatch,
  } = useProfileAppContext();
  const {
    state: {
      assets: { given, taken },
      orderRoute,
      derived: { transaction },
      flowData: { isUpdate, orderType },
    },
    dispatch,
  } = useOrderContext();
  const {
    details: { mainAddress },
  } = useWalletDetails();
  const [, setOrders] = useSubmittedOrdersCache(mainAddress);
  const { handleTransactionError } = useTransactionErrorHandler();
  const handleEventTracking = useEventTracking();
  const pool = usePoolFromOrderRoute();
  const version = pool.version ?? EContractVersion.V1;

  const referralFee = useMemo(() => {
    if (!checkIsEternlDappBridgeAvailable() || !given || !taken) return null;

    const referralFee = getEternlFeeAmount({
      given: { metadata: given?.metadata, quantity: given },
      taken: { metadata: taken?.metadata, quantity: taken },
    });

    const destination = getEternlFeeAddress();
    if (!destination) {
      throw new Error("No destination address found for referral fee");
    }

    return referralFee ? { destination, payment: referralFee } : null;
  }, [given, taken]);

  const configMultiPoolRoute = useMemo(() => {
    if (!given || !taken || !orderType || orderRoute?.length !== 2) {
      return;
    }

    const args = getMultiPoolRouteArgs({
      given,
      mainAddress,
      orderRoute,
      slippageTolerance,
    });

    if (referralFee) {
      args.swapA.referralFee = referralFee;
      args.swapB.referralFee = referralFee;
    }

    return args;
  }, []);

  const configSinglePoolRoute = useMemo(() => {
    if (!given || !taken || !orderType || orderRoute?.length !== 1) {
      return;
    }

    const args = getSinglePoolRouteArgs({
      given,
      isLimitOrder: orderType === EOrderType.limit,
      mainAddress,
      orderRoute,
      slippageTolerance,
      taken,
    });

    if (referralFee) args.referralFee = referralFee;

    return args;
  }, []);

  const handleCancelTx = () => {
    OrderActions.setOrderFlowState(
      EOrderFlowState.previewCancelled,
      dispatch,
      "useSwapTx.handleCancelTx"
    );
    Actions.setCollapsibleOpen({ collapsible: "orderFeeBreakdown", isOpen: false }, appDispatch);

    // Resetting the transaction builder state as we don't want to show previously calculated fees on the next swap screen.
    OrderActions.setTransaction({}, dispatch, "useSwapTx.handleCancelTx");
  };

  const buildTxMutation = useMutation(
    [configMultiPoolRoute, configSinglePoolRoute, sdk, isUpdate, version],
    async () => {
      if (!sdk) return;

      if (configSinglePoolRoute) {
        return TransactionErrorHandler.buildTransaction(async () => {
          if (isUpdate && utxo && address) {
            return sdk.builder(version as EContractVersion).update({
              cancelArgs: { ownerAddress: mainAddress, utxo },
              swapArgs: configSinglePoolRoute,
            });
          }

          return sdk.builder(version as EContractVersion).swap(configSinglePoolRoute);
        });
      }

      if (configMultiPoolRoute) {
        return TransactionErrorHandler.buildTransaction(async () =>
          sdk.builder(version as EContractVersion).orderRouteSwap(configMultiPoolRoute)
        );
      }
    },
    {
      cacheTime: 0,
      onMutate() {
        OrderActions.setTransactionState(
          EOrderTransactionBuilderState.BUILD,
          dispatch,
          "useSwapTx.buildTx.onMutate"
        );
      },
      onError({ error, transactionState }: ITransactionErrorShape) {
        OrderActions.setTransactionState(
          EOrderTransactionBuilderState.ERROR,
          dispatch,
          "useSwapTx.buildTx.onError"
        );

        handleTransactionError(
          error,
          {
            resolutionButtonText: t("transaction.error_reset"),
            resolutionAction: handleCancelTx,
          },
          transactionState,
          configMultiPoolRoute ?? configSinglePoolRoute
        );
      },
      async onSuccess(data: IComposedTx<Tx, TxComplete>) {
        if (!data) {
          return;
        }

        const completeTx = await data.build();
        OrderActions.setTransaction(
          { builtTx: data, completeTx, state: EOrderTransactionBuilderState.SUCCESS },
          dispatch,
          "useSwapTx.buildTx.onSuccess"
        );
      },
    }
  );

  const swapMutation = useMutation(
    [transaction?.builtTx],
    async () =>
      TransactionErrorHandler.handleSDKMutation(
        // we are not passing a buildFn here because we already have a built tx and the swap is only executed on the exchange preview screen.
        // this screen will only be available for users that have successfully built a tx.
        undefined,
        transaction?.builtTx
      ),
    {
      cacheTime: 0,
      onError({ error, transactionState, cbor }: ITransactionErrorShape) {
        handleTransactionError(
          error,
          {
            resolutionButtonText: t("transaction.error_reset"),
            resolutionAction: handleCancelTx,
          },
          transactionState,
          configMultiPoolRoute ?? configSinglePoolRoute,
          cbor
        );
      },
      onMutate() {
        handleEventTracking({
          eventType: EventType.Event,
          source: "Swap",
          value: `trade_execution_${orderType}`,
        });
        OrderActions.setOrderFlowState(
          EOrderFlowState.submitting,
          dispatch,
          "useSwapTx.builtTx.onMutate"
        );
      },
      onSuccess(hash) {
        if (orderRoute?.length === 1) {
          const pool = orderRoute[0];

          if (orderType === EOrderType.limit) {
            PersistentActions.addLimitOrder(hash, profileContextDispatch);
          }

          // set latestTxHash in app state
          Actions.setLatestTxHash(hash, appDispatch);

          const order: IPendingOrder = {
            date: new Date(),
            prevHash: isUpdate && utxo ? utxo.hash : undefined,
            hash,
            opType: EOpTypename.OpSwap,
            pool: pool,
            ...getSwapQuantitiesForOrders({
              estimatedInput: given ?? new AssetAmount(0, 6),
              inputAsset: given?.metadata,
              pool,
            }),
          };

          setOrders((prev) => [...prev, order]);
        }
        OrderActions.setOrderFlowState(
          EOrderFlowState.success,
          dispatch,
          "useSwapTx.builtTx.onSuccess"
        );
        return hash;
      },
    }
  );

  return {
    handleTxBuilding: buildTxMutation,
    handleSwap: swapMutation,
    handleCancelTx,
  };
};
